<script setup lang="ts">
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import Dialog from '@uiKit/elements/Dialog.vue'
import Message from '@uiKit/elements/Message.vue'
import { useToastStore } from '@uiKit/store/toastStore'
import { useSellerProfileQuery } from '@modules/profile'
import SupportTicketForm from '../components/SupportTicketForm.vue'
import { useTicketCategoriesQuery } from '../api/queries/useTicketCategoriesQuery'
import { useCreateSupportTicketMutation } from '../api/mutations/useCreateSupportTicketMutation'
import type { SupportTicketFormValues } from '../types/supportTypes'

// "Create Support Ticket" dialog for a signed-in seller (Figma 35143:147879).
// Owns the categories query, the seller profile (for the read-only name and
// email) and the create mutation; the form inside is presentational. The
// dialog is flush — this container draws the title and close control, the form
// draws the body and footer. PrimeVue's Dialog unmounts its content when
// hidden, so every opening starts blank.
const open = defineModel<boolean>({ required: true })

const toastStore = useToastStore()
const profileQuery = useSellerProfileQuery()
const categoriesQuery = useTicketCategoriesQuery()
const createMutation = useCreateSupportTicketMutation()

const categories = computed(() => categoriesQuery.data.value ?? [])
const contact = computed(() => {
  const profile = profileQuery.data.value
  return profile ? { name: profile.fullName, email: profile.email } : null
})
const apiError = computed(() => createMutation.error.value?.message ?? '')

function handleSubmit(values: SupportTicketFormValues) {
  createMutation.mutate(
    {
      categoryId: values.categoryId,
      subject: values.subject,
      description: values.description,
      attachment: values.attachment,
    },
    {
      onSuccess: (referenceCode) => {
        open.value = false
        toastStore.addToast({
          type: 'success',
          message: `Ticket ${referenceCode} submitted. We'll reply by email.`,
        })
      },
    },
  )
}

function handleClose() {
  if (createMutation.isPending.value) return
  open.value = false
  createMutation.reset()
}
</script>

<template>
  <!-- aria-label: PrimeVue's aria-labelledby points at its own header, which a
       custom #header replaces, so the dialog is named here instead. -->
  <Dialog
    :model-value="open"
    width="660px"
    is-flush
    :show-close-icon="false"
    aria-label="Create Support Ticket"
    @update:model-value="!$event && handleClose()"
  >
    <template #header>
      <Box flex align="center" justify="between" gap="4" class="create-ticket__head">
        <Text is="h2" type="title-lg" weight="semibold">Create Support Ticket</Text>
        <Button
          icon="times"
          variant="text"
          color="secondary"
          size="small"
          aria-label="Close"
          class="create-ticket__close"
          :disabled="createMutation.isPending.value"
          @click="handleClose"
        />
      </Box>
    </template>

    <Box class="create-ticket__scroll">
      <Box v-if="categoriesQuery.isError.value" padding="1.75rem">
        <Message color="danger" variant="outlined">
          <Box flex align="center" justify="between" gap="3" class="create-ticket__error">
            <Text is="span" type="body-sm" color="inherit">We couldn't load the categories.</Text>
            <Button
              label="Try again"
              variant="text"
              size="small"
              :loading="categoriesQuery.isFetching.value"
              @click="categoriesQuery.refetch()"
            />
          </Box>
        </Message>
      </Box>
      <SupportTicketForm
        v-else
        mode="signedIn"
        :contact="contact"
        :categories="categories"
        :is-categories-loading="categoriesQuery.isPending.value"
        :loading="createMutation.isPending.value"
        :api-error="apiError"
        @submit="handleSubmit"
        @cancel="handleClose"
      />
    </Box>
  </Dialog>
</template>

<style scoped>
.create-ticket__head {
  padding: 1.375rem 1.5rem 1.25rem 1.75rem;
  border-bottom: 1px solid var(--p-content-border-color);
}

/* The grey 32px dot around the X. The class lands on the .p-button root. */
.create-ticket__close {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 0;
  border-radius: 50%;
  background: var(--p-surface-100);
}

/* The flush panel caps its height; the form scrolls inside it on short
   viewports instead of pushing the footer off-screen. */
.create-ticket__scroll {
  min-height: 0;
  overflow-y: auto;
}

.create-ticket__error {
  flex-wrap: wrap;
  width: 100%;
}
</style>
